import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { TestDataResetDialog } from "@/components/TestDataResetDialog";
import { Copy, UserPlus, Users, Mail, Shield, Edit, Trash2, Check, X, Search, Calendar, ScrollText, KeyRound, Eye, EyeOff, GitBranch, RotateCcw, Activity, ShieldAlert, Ban, LogOut, Globe2 } from "lucide-react";
import AuditLogPanel from "./AuditLogPanel";
import { LiveUsersPanel } from "@/components/LiveUsersPanel";
import { SecurityFlagsPanel } from "@/components/SecurityFlagsPanel";
import { LoginMapPanel } from "@/components/LoginMapPanel";
import { UserSecurityDialog } from "@/components/UserSecurityDialog";
import { PanelRightsBox, RightsTags, ViewServicesBox } from "@/components/ExtraRightsBox";
import { OrderRegRightsBox } from "@/components/OrderRegRightsBox";
import { CopyAccessDialog } from "@/components/CopyAccessDialog";
import { PackedByNamesCard, PackerColumnsCard, PhotoHandlersCard, WrongPhotosCard } from "@/components/PackerColumnsCard";
import { PackerDailyCard } from "@/components/PackerDailyCard";
import { CorrectionAlertSettingsCard } from "@/components/CorrectionAlertSettingsCard";

const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [copyDlg, setCopyDlg] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const closeReset = useCallback(() => setResetUser(null), []);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [panelAccessDialogOpen, setPanelAccessDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserPanelAccess, setSelectedUserPanelAccess] = useState({});
  const [selectedUserDocSubPanels, setSelectedUserDocSubPanels] = useState({});
  const [selectedUserProcSubPanels, setSelectedUserProcSubPanels] = useState({});
  // Iter 177 — Order Register per-user scoping (services + country groups)
  const [orderRegServices, setOrderRegServices] = useState([]);
  const [orderRegAccess, setOrderRegAccess] = useState('editor');
  const [orderRegButtons, setOrderRegButtons] = useState(null);
  const [orderRegViewServices, setOrderRegViewServices] = useState([]);
  const [panelRights, setPanelRights] = useState({});
  const [orderRegCountries, setOrderRegCountries] = useState([]);
  const [orderRegOptions, setOrderRegOptions] = useState({
    services: ["CMS", "DHL", "DIFF_SVC", "EMIRATES", "EMS", "EMS-SERVICE", "FEDEX",
               "ITPS", "ITPS-USA", "ITPS-UK", "ITPS-Australia", "ITPS-Korea",
               "ITPS (Other Countries)", "RMS", "SPEED POST", "USPS", "FREESHIP"],
    countryGroups: ["USA", "AUSTRALIA", "UK", "SOUTH_KOREA", "JAPAN", "CHINA", "OTHERS"],
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMoveTeam, setBulkMoveTeam] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["audit", "live", "security", "map"].includes(t) ? t : "members";
  });

  // Role templates (permission presets)
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [manageTemplatesOpen, setManageTemplatesOpen] = useState(false);

  // Refactor trend badge — read-only summary of server.py → routes/ extraction
  // progress, parsed server-side from /app/memory/REFACTOR_LOG.md (admin-only API)
  const [refactorInfo, setRefactorInfo] = useState(null);
  const [showRefactorLog, setShowRefactorLog] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/refactor-progress`, { headers: getAuthHeader() })
      .then((r) => setRefactorInfo(r.data))
      .catch(() => setRefactorInfo(null));
  }, []);

  const fetchRoleTemplates = async () => {
    try {
      const r = await axios.get(`${API}/admin/role-templates`, { headers: getAuthHeader() });
      setRoleTemplates(r.data.templates || []);
    } catch { /* ignore */ }
  };

  const applyTemplate = (templateId) => {
    if (templateId === "__none__") return;
    const t = roleTemplates.find(x => x.id === templateId);
    if (!t) return;
    setSelectedUserPanelAccess({ ...(t.panel_access || {}) });
    setSelectedUserDocSubPanels({ ...(t.document_sub_panels || {}) });
    setSelectedUserProcSubPanels({ ...(t.processing_team_sub_panels || {}) });
    toast.success(`Applied template "${t.name}"`);
  };

  const saveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) { toast.error("Please enter a template name"); return; }
    try {
      await axios.post(`${API}/admin/role-templates`, {
        name, description: templateDesc,
        panel_access: selectedUserPanelAccess,
        document_sub_panels: selectedUserDocSubPanels,
        processing_team_sub_panels: selectedUserProcSubPanels,
      }, { headers: getAuthHeader() });
      toast.success(`Template "${name}" saved`);
      setSaveTemplateOpen(false);
      setTemplateName("");
      setTemplateDesc("");
      await fetchRoleTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save template");
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/role-templates/${t.id}`, { headers: getAuthHeader() });
      toast.success("Template deleted");
      await fetchRoleTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    team: "",
    role: "member",
    // DEFAULT-DENY: new user starts with NO permissions. Admin must
    // explicitly tick each panel/sub-panel they want the user to access.
    panel_access: {},
    document_sub_panels: {},
    processing_team_sub_panels: {}
  });

  const teams = [
    { value: "Account", label: "Account Team" },
    { value: "Processing", label: "Processing Team" },
    { value: "BackOffice", label: "Back Office Team" },
    { value: "ITDept", label: "IT Department" },
    { value: "Partners", label: "Partners Team" }
  ];

  // Module registry — fetched dynamically from backend so new sub-panels
  // added anywhere in the app automatically appear here without client edits.
  const [registry, setRegistry] = useState({
    panels: [],
    document_sub_panels: [],
    processing_team_sub_panels: [],
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/modules/registry`, { headers: getAuthHeader() })
      .then((r) => { if (!cancelled) setRegistry(r.data); })
      .catch(() => { /* keep empty fallback */ });
    return () => { cancelled = true; };
  }, []);

  const panels = (registry.panels || []).map((p) => ({
    key: p.id, label: p.label, description: p.description,
    sub_panels_key: p.sub_panels_key,
  }));
  const documentSubPanels = (registry.document_sub_panels || []).map((p) => ({
    key: p.id, label: p.label, description: p.description,
  }));
  const processingSubPanels = (registry.processing_team_sub_panels || []).map((p) => ({
    key: p.id, label: p.label, description: p.description,
  }));

  // Team stats computed from users
  const teamStats = useMemo(() => {
    const stats = {};
    const allTeams = ['Account', 'Processing', 'BackOffice', 'ITDept', 'Partners', 'Admin'];
    allTeams.forEach(t => { stats[t] = { total: 0, active: 0, pending: 0 }; });
    users.forEach(u => {
      const t = u.team || 'Other';
      if (!stats[t]) stats[t] = { total: 0, active: 0, pending: 0 };
      stats[t].total++;
      if (u.approved) stats[t].active++;
      else stats[t].pending++;
    });
    return stats;
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      if (teamFilter !== 'all' && u.team !== teamFilter) return false;
      if (statusFilter === 'approved' && !u.approved) return false;
      if (statusFilter === 'pending' && u.approved) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [users, teamFilter, statusFilter, searchQuery]);

  const teamCardConfig = [
    { key: 'Account', label: 'Account', icon: '📋', bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-blue-500', text: 'text-blue-700' },
    { key: 'Processing', label: 'Processing', icon: '⚙️', bg: 'bg-purple-50', border: 'border-purple-200', iconBg: 'bg-purple-500', text: 'text-purple-700' },
    { key: 'BackOffice', label: 'Back Office', icon: '📁', bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-500', text: 'text-green-700' },
    { key: 'ITDept', label: 'IT Department', icon: '💻', bg: 'bg-orange-50', border: 'border-orange-200', iconBg: 'bg-orange-500', text: 'text-orange-700' },
    { key: 'Partners', label: 'Partners', icon: '🤝', bg: 'bg-pink-50', border: 'border-pink-200', iconBg: 'bg-pink-500', text: 'text-pink-700' },
    { key: 'Admin', label: 'Admin', icon: '🛡️', bg: 'bg-red-50', border: 'border-red-200', iconBg: 'bg-red-500', text: 'text-red-700' },
  ];

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleBulkAction = async (action, team) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const label = action === 'approve' ? 'approve' : action === 'delete' ? 'delete' : action === 'reject' ? 'reject' : `move to ${team}`;
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${ids.length} user(s)?`)) return;
    try {
      const res = await axios.post(`${API}/admin/bulk-action`, { user_ids: ids, action, team }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCoverageGaps();
  }, []);

  // Iter 182 — Coverage Gaps banner
  const [coverageGaps, setCoverageGaps] = useState(null);
  const fetchCoverageGaps = async () => {
    try {
      const r = await axios.get(`${API}/admin/coverage-gaps`, { headers: getAuthHeader() });
      setCoverageGaps(r.data);
    } catch { /* non-admin viewer or API off — banner just stays hidden */ }
  };

  const [presence, setPresence] = useState({});
  const [secUser, setSecUser] = useState(null);
  const fetchPresence = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/security/presence`, { headers: getAuthHeader() });
      setPresence(r.data || {});
    } catch { /* non-admin or offline */ }
  }, []);
  useEffect(() => { fetchPresence(); const t = setInterval(fetchPresence, 30000); return () => clearInterval(t); }, [fetchPresence]);
  const secAction = async (u, action) => {
    const label = { disable: "Disable", enable: "Re-enable", "force-logout": "Force logout" }[action];
    let body = {};
    if (action === "disable") {
      const reason = window.prompt(`Disable ${u.name}? They are logged out everywhere and cannot log in until re-enabled.\nReason (optional):`, "");
      if (reason === null) return;
      body = { reason };
    } else if (!window.confirm(`${label} ${u.name}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/${action}`, body, { headers: getAuthHeader() });
      toast.success(`${label}: ${u.name}`);
      fetchUsers(); fetchPresence();
    } catch (e) { toast.error(e.response?.data?.detail || `${label} failed`); }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/create-user`, newUser, { headers: getAuthHeader() });
      toast.success("User created successfully!");
      setCreateDialogOpen(false);
      setNewUser({
        email: "",
        password: "",
        name: "",
        team: "",
        role: "member",
        panel_access: {},
        document_sub_panels: {},
        processing_team_sub_panels: {}
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, {
        name: selectedUser.name,
        team: selectedUser.team,
        role: selectedUser.role,
        panel_access: selectedUser.panel_access,
        document_sub_panels: selectedUser.document_sub_panels,
        processing_team_sub_panels: selectedUser.processing_team_sub_panels,
        is_pp_user: !!selectedUser.is_pp_user,
        pp_hours: selectedUser.pp_hours || 48,
        is_packer: !!selectedUser.is_packer,
        ...(selectedUser.is_packer ? { packer_services: selectedUser.packer_services || [] } : {}),
        is_query_team: !!selectedUser.is_query_team,
        pending_service_report: !!selectedUser.pending_service_report,
        ...(selectedUser.email !== 'admin@taskflow.com' ? { is_test_account: !!selectedUser.is_test_account } : {})
      }, { headers: getAuthHeader() });
      toast.success("User updated successfully!");
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success("User deleted successfully!");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Admin view / change any member's password
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [currentPassword, setCurrentPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const openPasswordDialog = async (user) => {
    setPasswordTarget(user);
    setCurrentPassword(null);
    setNewPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setPasswordDialogOpen(true);
    try {
      const r = await axios.get(`${API}/admin/users/${user.id}/password`, { headers: getAuthHeader() });
      setCurrentPassword(r.data.password || null);
    } catch { setCurrentPassword(null); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordTarget) return;
    if (newPassword.trim().length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPasswordSaving(true);
    try {
      await axios.put(`${API}/admin/users/${passwordTarget.id}/password`, { new_password: newPassword.trim() }, { headers: getAuthHeader() });
      toast.success(`Password updated for ${passwordTarget.email}`);
      setCurrentPassword(newPassword.trim());
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setPasswordSaving(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser({
      ...user,
      panel_access: user.panel_access || {
        dashboard: true, tasks: true, recurring: true, notes: true,
        projects: true, documents: true, mindmap: true, reports: true,
        chat: true, queries: true
      },
      document_sub_panels: user.document_sub_panels || {
        excel_transpose: true, pdf_edits: true, packing_list: true,
        bulk_data: true, pack_team_print: true, pdf_to_word: true,
        bulk_label: true, manage_companies: true
      }
    });
    setEditDialogOpen(true);
  };

  const openPanelAccessDialog = (user) => {
    setSelectedUser(user);
    // DEFAULT-DENY: keep exactly what's stored on the user record.
    // Missing keys stay missing → they will render as unchecked and remain denied on save.
    setSelectedUserPanelAccess(user.panel_access || {});
    setSelectedUserDocSubPanels(user.document_sub_panels || {});
    setSelectedUserProcSubPanels(user.processing_team_sub_panels || {});
    setOrderRegServices(user.order_register_allowed_services || []);
    setOrderRegCountries(user.order_register_allowed_country_groups || []);
    setOrderRegAccess(user.order_register_access || 'editor');
    setOrderRegViewServices(user.order_register_view_services || []);
    setPanelRights(user.panel_rights || {});
    setOrderRegButtons(user.order_register_allowed_buttons ?? null);
    setPanelAccessDialogOpen(true);
    fetchRoleTemplates();
    fetchOrderRegScopeOptions();
  };

  // Iter 177 — fetch the canonical service + country-group option lists
  // for the Order Register scoping UI (falls back to built-in lists).
  // Iter 181 — one-click scope presets (Service pair + Country group)
  const ORDERREG_PRESETS = [
    { key: 'usa',     label: 'USA desk',       services: ['ITPS-USA'],              countries: ['USA'] },
    { key: 'uk',      label: 'UK desk',        services: ['ITPS-UK'],               countries: ['UK'] },
    { key: 'au',      label: 'Australia desk', services: ['ITPS-Australia'],        countries: ['AUSTRALIA'] },
    { key: 'kr',      label: 'Korea desk',     services: ['ITPS-Korea'],            countries: ['SOUTH_KOREA'] },
    { key: 'others',  label: 'Others desk',    services: ['ITPS (Other Countries)'], countries: ['JAPAN','CHINA','OTHERS'] },
    { key: 'emirates',label: 'Emirates desk',  services: ['EMIRATES'],              countries: ['USA','AUSTRALIA','UK','SOUTH_KOREA','JAPAN','CHINA','OTHERS'] },
  ];
  const applyOrderRegPreset = (p) => {
    setOrderRegServices(p.services);
    setOrderRegCountries(p.countries);
  };

  // Iter 181 — Compact human-readable "effective coverage" summary shown
  // under each user row (assignment summary). "—" for admin / no grants.
  const summarizeOrderRegScope = (user) => {
    if (user.role === 'admin') return { text: 'Admin — unrestricted', tone: 'admin' };
    const s = user.order_register_allowed_services || [];
    const c = user.order_register_allowed_country_groups || [];
    const proc = user.processing_team_sub_panels || {};
    if (!proc.order_register) return null; // panel not granted → don't clutter
    if (user.is_query_team && !user.is_pp_user && s.length === 0 && c.length === 0) return { text: 'Query team · all parcels (read-only + query thread)', tone: 'ok' };
    if (user.is_pp_user) return { text: `PP · ${user.pp_hours || 48}h+ pending · ${s.length ? s.join(', ') : 'all services'} · ${c.length ? c.join(', ').replace(/_/g, ' ') : 'all countries'}`, tone: 'ok' };
    if (s.length === 0 || c.length === 0) return { text: 'No orders (grant a service + country)', tone: 'empty' };
    const svcTxt = s.length > 4 ? `${s.slice(0, 4).join(', ')} +${s.length - 4}` : s.join(', ');
    const ctyTxt = c.length > 4 ? `${c.slice(0, 4).join(', ').replace(/_/g,' ')} +${c.length - 4}` : c.join(', ').replace(/_/g,' ');
    const rights = (user.order_register_access === 'viewer' ? 'VIEWER' : 'Editor')
      + (Array.isArray(user.order_register_allowed_buttons) ? ` · ${user.order_register_allowed_buttons.length} buttons` : '');
    return { text: `${rights}  ·  ${svcTxt}  ·  ${ctyTxt}`, tone: 'ok' };
  };

  const fetchOrderRegScopeOptions = async () => {
    try {
      const r = await axios.get(`${API}/order-register/reasons`, { headers: getAuthHeader() });
      setOrderRegOptions(prev => ({
        services: (r.data.services && r.data.services.length) ? r.data.services : prev.services,
        countryGroups: (r.data.country_groups && r.data.country_groups.length) ? r.data.country_groups : prev.countryGroups,
      }));
    } catch { /* keep fallback lists */ }
  };

  // Detect parents that were toggled ON but have NO sub-panels selected — the
  // user would see the panel in the sidebar and get Access Denied inside.
  // Documents & Processing Team are the two parents that gate on sub-panels.
  const getParentOnlyMisconfig = () => {
    const bad = [];
    const docSubOn = Object.values(selectedUserDocSubPanels || {}).some((v) => v === true);
    const procSubOn = Object.values(selectedUserProcSubPanels || {}).some((v) => v === true);
    if (selectedUserPanelAccess?.documents === true && !docSubOn) {
      bad.push({ label: "Documents", suggestion: "Grant Full Panel", key: "documents" });
    }
    if (selectedUserPanelAccess?.processing_team === true && !procSubOn) {
      bad.push({ label: "Processing Team", suggestion: "Grant Full Panel", key: "processing_team" });
    }
    return bad;
  };

  const handleUpdatePanelAccess = async () => {
    if (!selectedUser) return;
    // Guard: warn if admin granted a parent panel but no sub-panels.
    const misconfig = getParentOnlyMisconfig();
    if (misconfig.length > 0) {
      const list = misconfig.map(m => `• ${m.label}`).join("\n");
      const proceed = window.confirm(
        `Warning — the user will see 'Access Denied' when clicking the following:\n\n${list}\n\n` +
        `You've enabled the parent panel but haven't ticked any of its sub-panels. Either:\n` +
        `  · Click "Grant Full Panel" next to the parent, OR\n` +
        `  · Tick the specific sub-panels the user should access.\n\n` +
        `Click OK to save anyway (user will hit Access Denied), or Cancel to fix it first.`
      );
      if (!proceed) return;
    }
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}/panel-access`,
        {
          panel_access: selectedUserPanelAccess,
          document_sub_panels: selectedUserDocSubPanels,
          processing_team_sub_panels: selectedUserProcSubPanels,
          order_register_allowed_services: orderRegServices,
          order_register_allowed_country_groups: orderRegCountries,
          order_register_access: orderRegAccess,
          order_register_allowed_buttons: orderRegButtons,
          order_register_view_services: orderRegViewServices,
          panel_rights: panelRights,
        },
        { headers: getAuthHeader() }
      );
      toast.success("Panel access updated successfully!");
      setPanelAccessDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
      fetchCoverageGaps();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update panel access");
    }
  };

  const getTeamColor = (team) => {
    const colors = {
      'Account': 'bg-blue-100 text-blue-700 border-blue-200',
      'Processing': 'bg-purple-100 text-purple-700 border-purple-200',
      'BackOffice': 'bg-green-100 text-green-700 border-green-200',
      'ITDept': 'bg-orange-100 text-orange-700 border-orange-200',
      'Partners': 'bg-pink-100 text-pink-700 border-pink-200',
      'Admin': 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[team] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getRoleColor = (role) => {
    return role === 'admin' 
      ? 'bg-red-100 text-red-700 border-red-200' 
      : 'bg-slate-100 text-slate-700 border-slate-200';
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="space-y-5" data-testid="user-management-panel">
      <TestDataResetDialog user={resetUser} onClose={closeReset} />
      <PackerColumnsCard />
      <PhotoHandlersCard />
      <PackedByNamesCard />
      <WrongPhotosCard />
      <PackerDailyCard />
      <CorrectionAlertSettingsCard />
      {/* Refactor Trend Badge — server.py → routes/ extraction progress */}
      {refactorInfo && (
        <Card className="border-emerald-200 bg-emerald-50/50 shadow-none" data-testid="refactor-trend-card">
          <CardContent className="py-2.5 px-4">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-semibold text-slate-800">Backend Refactor</span>
                <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-white" data-testid="refactor-pct-badge">
                  {refactorInfo.pct_reduced}% smaller
                </Badge>
              </div>
              <span className="text-xs text-slate-600" data-testid="refactor-lines">
                server.py <b>{Number(refactorInfo.start_lines).toLocaleString()}</b> → <b>{Number(refactorInfo.current_lines).toLocaleString()}</b> lines
              </span>
              <span className="text-xs text-slate-600" data-testid="refactor-routes">
                routes: <b>{refactorInfo.routes_in_modules}</b> in modules · <b>{refactorInfo.routes_left}</b> left
              </span>
              <span className="text-xs text-slate-500 truncate max-w-md" title={refactorInfo.last_label} data-testid="refactor-last">
                last: {refactorInfo.last_label} · {refactorInfo.last_date}
              </span>
              <button type="button" className="text-xs text-indigo-600 hover:underline ml-auto shrink-0"
                      data-testid="refactor-log-toggle" onClick={() => setShowRefactorLog(v => !v)}>
                {showRefactorLog ? "Hide history" : `History (${refactorInfo.extractions})`}
              </button>
            </div>
            {showRefactorLog && (
              <div className="mt-2 border-t border-emerald-200 pt-2 space-y-1" data-testid="refactor-log-list">
                {refactorInfo.entries.map((e, i) => (
                  <div key={i} className="text-xs text-slate-600 flex gap-3 items-baseline" data-testid={`refactor-log-row-${i}`}>
                    <span className="text-slate-400 shrink-0">{e.date}</span>
                    <span className="font-mono shrink-0 text-emerald-700">{e.lines}</span>
                    <span className="truncate" title={e.label}>{e.label}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Iter 182 — Coverage Gaps Alert (Order Register) */}
      {coverageGaps && (coverageGaps.uncovered_country_groups?.length > 0 ||
                         coverageGaps.uncovered_services?.length > 0) && (
        <div className="rounded-lg border-2 border-rose-400 bg-rose-50 p-3"
             data-testid="coverage-gaps-banner">
          <div className="flex items-start gap-2">
            <Shield className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="text-xs text-rose-900 space-y-1 flex-1">
              <p className="font-semibold text-rose-800">
                Order Register — coverage gaps ({coverageGaps.total_processors} processors)
              </p>
              {coverageGaps.uncovered_country_groups?.length > 0 && (
                <p data-testid="coverage-gaps-countries">
                  <strong>No processor for:</strong>{' '}
                  {coverageGaps.uncovered_country_groups.map(c => (
                    <span key={c} className="inline-block px-1.5 py-0.5 rounded-full bg-rose-200 text-rose-900 text-[10px] font-semibold mr-1">
                      {c.replace(/_/g, ' ')}
                    </span>
                  ))}
                  — orders for these countries will pile up unassigned.
                </p>
              )}
              {coverageGaps.uncovered_services?.length > 0 && (
                <p data-testid="coverage-gaps-services">
                  <strong>No processor for services:</strong>{' '}
                  {coverageGaps.uncovered_services.map(s => (
                    <span key={s} className="inline-block px-1.5 py-0.5 rounded-full bg-rose-200 text-rose-900 text-[10px] font-semibold mr-1">
                      {s}
                    </span>
                  ))}
                </p>
              )}
              <p className="text-rose-700 text-[10px]">
                Assign an approved processor with the matching service + country grant using the shield icon on any row.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="team-summary-cards">
        {teamCardConfig.map(tc => {
          const s = teamStats[tc.key] || { total: 0, active: 0, pending: 0 };
          return (
            <div
              key={tc.key}
              onClick={() => setTeamFilter(teamFilter === tc.key ? 'all' : tc.key)}
              className={`rounded-xl border p-3 cursor-pointer transition-all hover:shadow-md ${
                teamFilter === tc.key ? `${tc.bg} ${tc.border} ring-2 ring-offset-1 ring-${tc.key === 'Admin' ? 'red' : tc.key === 'Account' ? 'blue' : 'indigo'}-300` : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`team-card-${tc.key}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-8 h-8 ${tc.iconBg} rounded-lg flex items-center justify-center text-white text-sm`}>
                  {tc.icon}
                </div>
                <span className="text-2xl font-bold text-slate-900">{s.total}</span>
              </div>
              <p className={`text-xs font-semibold ${tc.text} truncate`}>{tc.label}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {s.active} active, {s.pending} pending
              </p>
            </div>
          );
        })}
      </div>

      {/* Tabs: Members vs Audit Log */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto [&>button]:shrink-0 [&>button]:whitespace-nowrap" data-testid="user-mgmt-tabs">
        <button
          onClick={() => setActiveTab("members")}
          data-testid="tab-members"
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all ${
            activeTab === "members"
              ? "border-purple-600 text-purple-700"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Users className="w-4 h-4" />
          Members
          <Badge className="bg-slate-100 text-slate-600 text-[10px] ml-1">{users.length}</Badge>
        </button>
        <button
          onClick={() => setActiveTab("audit")}
          data-testid="tab-audit"
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all ${
            activeTab === "audit"
              ? "border-purple-600 text-purple-700"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <ScrollText className="w-4 h-4" />
          Audit Log
        </button>
        {[
          { k: "live", label: "Live Users", Icon: Activity, n: Object.keys(presence).length },
          { k: "security", label: "Security", Icon: ShieldAlert,
            n: users.reduce((a, u) => a + (u.known_ips || []).filter(x => !x.verified).length, 0) },
          { k: "map", label: "Login Map", Icon: Globe2, n: 0 },
        ].map(({ k, label, Icon, n }) => (
          <button key={k} onClick={() => setActiveTab(k)} data-testid={`tab-${k}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all ${
              activeTab === k ? "border-purple-600 text-purple-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="w-4 h-4" /> {label}
            {n > 0 && <Badge className={`${k === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"} text-[10px] ml-1`}>{n}</Badge>}
          </button>
        ))}
      </div>
      <UserSecurityDialog user={secUser} onClose={() => setSecUser(null)} />

      {activeTab === "live" ? (
        <LiveUsersPanel />
      ) : activeTab === "security" ? (
        <SecurityFlagsPanel onChanged={fetchUsers} />
      ) : activeTab === "map" ? (
        <LoginMapPanel />
      ) : activeTab === "audit" ? (
        <AuditLogPanel />
      ) : (
      <>
      {/* Main Content Card */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  User Management
                </CardTitle>
                <CardDescription>
                  {filteredUsers.length} of {users.length} members
                </CardDescription>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-48 h-9 text-sm"
                  data-testid="user-search-input"
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32 h-9 text-sm" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>

              {/* Create User Button */}
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="create-user-button"
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white h-9"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Create a new user account with specific panel access
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      data-testid="new-user-name"
                      placeholder="John Doe"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="new-user-email"
                      placeholder="john@company.com"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      data-testid="new-user-password"
                      placeholder="••••••••"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="team">Team</Label>
                    <Select 
                      onValueChange={(value) => setNewUser({ ...newUser, team: value })} 
                      value={newUser.team}
                    >
                      <SelectTrigger data-testid="new-user-team">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(team => (
                          <SelectItem key={team.value} value={team.value}>{team.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select 
                    onValueChange={(value) => setNewUser(value === 'packer'
                      ? { ...newUser, role: 'member', is_packer: true, is_pp_user: false, is_query_team: false }
                      : (value === 'pp_user' || value === 'query_team')
                      ? { ...newUser, is_packer: false, role: 'member', is_pp_user: value === 'pp_user', is_query_team: value === 'query_team',
                          panel_access: { ...(newUser.panel_access || {}), processing_team: true },
                          processing_team_sub_panels: { ...(newUser.processing_team_sub_panels || {}), order_register: true } }
                      : { ...newUser, role: value, is_pp_user: false, is_query_team: false, is_packer: false })}
                    value={newUser.is_packer ? 'packer' : newUser.is_pp_user ? 'pp_user' : newUser.is_query_team ? 'query_team' : newUser.role}
                  >
                    <SelectTrigger data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="pp_user" data-testid="new-user-role-pp">PP User (Pending Parcel User)</SelectItem>
                      <SelectItem value="query_team" data-testid="new-user-role-query">Query Team (customer queries only)</SelectItem>
                      <SelectItem value="packer" data-testid="new-user-role-packer">Packing Team (phone camera · photos only)</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Panel Access */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Panel Access</Label>
                  <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                    {panels.map(panel => (
                      <div key={panel.key} className="flex items-center justify-between py-1">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`new-${panel.key}`}
                            data-testid={`new-panel-${panel.key}`}
                            checked={newUser.panel_access[panel.key]}
                            onCheckedChange={(checked) => setNewUser({
                              ...newUser,
                              panel_access: { ...newUser.panel_access, [panel.key]: checked }
                            })}
                          />
                          <label htmlFor={`new-${panel.key}`} className="text-sm font-medium cursor-pointer">
                            {panel.label}
                          </label>
                        </div>
                        <span className="text-xs text-slate-500">{panel.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Document Sub-Panel Access */}
                {newUser.panel_access.documents && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-purple-700">Documents Panel Permissions</Label>
                    <div className="border border-purple-200 rounded-lg p-3 space-y-2 bg-purple-50/50">
                      {documentSubPanels.map(sp => (
                        <div key={sp.key} className="flex items-center justify-between py-1">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`new-doc-${sp.key}`}
                              data-testid={`new-doc-panel-${sp.key}`}
                              checked={newUser.document_sub_panels[sp.key]}
                              onCheckedChange={(checked) => setNewUser({
                                ...newUser,
                                document_sub_panels: { ...newUser.document_sub_panels, [sp.key]: checked }
                              })}
                            />
                            <label htmlFor={`new-doc-${sp.key}`} className="text-sm font-medium cursor-pointer">
                              {sp.label}
                            </label>
                          </div>
                          <span className="text-xs text-slate-500">{sp.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processing Team Sub-Panels - shown when Processing Team is enabled */}
                {newUser.panel_access.processing_team && (
                  <div className="ml-6 pl-4 border-l-2 border-emerald-200 space-y-2">
                    <p className="text-xs font-medium text-emerald-600">Processing Team Sub-Panels</p>
                    <div className="space-y-1.5">
                      {processingSubPanels.map(sp => (
                        <div key={sp.key} className="flex items-center justify-between p-2 rounded border border-slate-100 bg-white">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`new-proc-${sp.key}`}
                              data-testid={`new-proc-panel-${sp.key}`}
                              checked={newUser.processing_team_sub_panels?.[sp.key] || false}
                              onCheckedChange={(checked) => setNewUser({
                                ...newUser,
                                processing_team_sub_panels: { ...(newUser.processing_team_sub_panels || {}), [sp.key]: checked }
                              })}
                            />
                            <label htmlFor={`new-proc-${sp.key}`} className="text-sm font-medium cursor-pointer">
                              {sp.label}
                            </label>
                          </div>
                          <span className="text-xs text-slate-500">{sp.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  data-testid="create-user-submit"
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
                >
                  Create User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
            </div>
          </div>

          {/* Team Filter Tabs */}
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t" data-testid="team-filter-tabs">
            <button
              onClick={() => setTeamFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                teamFilter === 'all'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="filter-tab-all"
            >
              All Teams ({users.length})
            </button>
            {teamCardConfig.map(tc => {
              const count = (teamStats[tc.key] || { total: 0 }).total;
              if (count === 0) return null;
              return (
                <button
                  key={tc.key}
                  onClick={() => setTeamFilter(teamFilter === tc.key ? 'all' : tc.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    teamFilter === tc.key
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-tab-${tc.key}`}
                >
                  {tc.label} ({count})
                </button>
              );
            })}
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="user-bulk-bar">
              <span className="text-sm font-medium text-indigo-800">{selectedIds.size} selected</span>
              <div className="h-4 w-px bg-indigo-300" />
              <Button size="sm" variant="outline" className="h-7 text-xs text-green-700 border-green-300 hover:bg-green-50" data-testid="user-bulk-approve" onClick={() => handleBulkAction('approve')}>
                <Check className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-50" data-testid="user-bulk-copy-access"
                onClick={() => setCopyDlg({ source: null, targets: [...selectedIds] })}>
                <Copy className="w-3 h-3 mr-1" /> Copy access to selected
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50" data-testid="user-bulk-delete" onClick={() => handleBulkAction('delete')}>
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
              <div className="flex items-center gap-1">
                <Select value={bulkMoveTeam} onValueChange={setBulkMoveTeam}>
                  <SelectTrigger className="h-7 w-36 text-xs" data-testid="user-bulk-move-select">
                    <SelectValue placeholder="Move to team..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    <SelectItem value="Admin">Admin Panel</SelectItem>
                  </SelectContent>
                </Select>
                {bulkMoveTeam && (
                  <Button size="sm" variant="outline" className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="user-bulk-move-btn" onClick={() => { handleBulkAction('move_team', bulkMoveTeam); setBulkMoveTeam(''); }}>
                    Move
                  </Button>
                )}
              </div>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 ml-auto" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
            </div>
          )}

          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">
                {searchQuery || teamFilter !== 'all' || statusFilter !== 'all' ? 'No users match your filters' : 'No users found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm max-md:block" data-testid="users-table">
                <thead className="max-md:hidden">
                  <tr className="border-b bg-slate-50/80">
                    <th className="px-3 py-2.5 w-10">
                      <Checkbox
                        checked={filteredUsers.length > 0 && selectedIds.size === filteredUsers.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="user-select-all"
                      />
                    </th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Member</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Team</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Role</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Status</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Joined</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="max-md:block max-md:space-y-2">
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      data-testid={`user-row-${user.id}`}
                      className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors max-md:flex max-md:flex-wrap max-md:items-center max-md:gap-2 max-md:rounded-xl max-md:border max-md:border-slate-200 max-md:p-3 max-md:shadow-sm ${selectedIds.has(user.id) ? 'bg-indigo-50/50' : 'max-md:bg-white'}`}
                    >
                      <td className="px-3 py-3 w-10 max-md:p-0 max-md:w-auto">
                        <Checkbox
                          checked={selectedIds.has(user.id)}
                          onCheckedChange={() => toggleSelect(user.id)}
                          data-testid={`user-select-${user.id}`}
                        />
                      </td>
                      <td className="px-3 py-3 max-md:p-0 max-md:basis-[calc(100%-2rem)] max-md:min-w-0">
                        <div className="flex items-center gap-2.5">
                          <Avatar className="w-9 h-9 shrink-0">
                            <AvatarFallback className={`${
                              user.role === 'admin' ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                            } text-white font-semibold text-xs`}>
                              {user.name?.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate text-sm flex items-center gap-1.5">
                              {user.name}
                              {user.is_test_account && (
                                <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200"
                                      data-testid={`test-account-badge-${user.id}`}>Test</span>
                              )}
                              {user.is_query_team && user.role !== 'admin' && (
                                <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-300"
                                      data-testid={`query-team-badge-${user.id}`}>Query Team</span>
                              )}
                              {user.is_packer && user.role !== 'admin' && (
                                <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-300"
                                      title="Packing Team — phone camera screen, photos only"
                                      data-testid={`packer-badge-${user.id}`}>Packing Team</span>
                              )}
                              {user.is_pp_user && user.role !== 'admin' && (
                                <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300"
                                      title={`Pending Parcel user — sees only ${user.pp_hours || 48}h+ pending parcels`}
                                      data-testid={`pp-user-badge-${user.id}`}>PP User · {user.pp_hours || 48}h</span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3 shrink-0" />{user.email}
                            </p>
                            <RightsTags u={user} />
                            {(() => {
                              const s = summarizeOrderRegScope(user);
                              if (!s) return null;
                              const tone = s.tone === 'admin' ? 'text-amber-700 bg-amber-50 border-amber-200'
                                          : s.tone === 'empty' ? 'text-rose-700 bg-rose-50 border-rose-200'
                                          : 'text-emerald-700 bg-emerald-50 border-emerald-200';
                              return (
                                <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${tone} max-w-full truncate`}
                                     data-testid={`orderreg-summary-${user.id}`}
                                     title={s.text}>
                                  <Shield className="w-2.5 h-2.5 shrink-0" />
                                  <span className="truncate">OR: {s.text}</span>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 max-md:p-0 max-md:pl-6">
                        <Badge className={`${getTeamColor(user.team)} border text-xs font-medium`}>
                          {user.team}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-center max-md:p-0">
                        <Badge className={`${getRoleColor(user.role)} border text-xs`}>
                          {user.role === 'admin' && <Shield className="w-3 h-3 mr-0.5" />}
                          {user.role}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-center max-md:p-0">
                        <Badge className={`text-xs ${
                          user.approved
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-amber-100 text-amber-700 border-amber-200'
                        }`} variant="outline">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${user.approved ? 'bg-green-500' : 'bg-amber-500'}`} />
                          {user.approved ? 'Approved' : 'Pending'}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap max-md:p-0 max-md:basis-full max-md:pl-6 max-md:whitespace-normal">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                          {presence[user.id] ? (
                            <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold"
                                  title={`Live · IP ${presence[user.id].ip}`} data-testid={`user-live-${user.id}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
                            </span>
                          ) : user.last_seen ? (
                            <span className="ml-1.5 text-[10px] text-slate-400" title="Last seen">· {new Date(user.last_seen).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                          ) : null}
                          {user.disabled && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold" title={user.disabled_reason || ''}
                                  data-testid={`user-disabled-${user.id}`}>DISABLED</span>
                          )}
                          {(user.known_ips || []).some(x => !x.verified) && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold" title="Unverified new IP"
                                  data-testid={`user-newip-${user.id}`}>NEW IP</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 max-md:p-0 max-md:basis-full max-md:border-t max-md:border-slate-100 max-md:pt-2">
                        <div className="flex items-center justify-center gap-1 max-md:justify-between max-md:[&_button]:h-9 max-md:[&_button]:w-9 max-md:[&_svg]:h-4 max-md:[&_svg]:w-4">
                          <Button variant="ghost" size="sm" onClick={() => setSecUser(user)}
                            data-testid={`user-security-${user.id}`} title="Security: IPs, login history, sessions"
                            className="h-7 w-7 p-0 text-slate-500 hover:text-amber-600 hover:bg-amber-50">
                            <ShieldAlert className="w-3.5 h-3.5" />
                          </Button>
                          {user.role !== 'admin' && (<>
                            <Button variant="ghost" size="sm" onClick={() => secAction(user, user.disabled ? 'enable' : 'disable')}
                              data-testid={`user-${user.disabled ? 'enable' : 'disable'}-${user.id}`}
                              title={user.disabled ? 'Re-enable user' : 'Disable temporarily (logs out everywhere)'}
                              className={`h-7 w-7 p-0 ${user.disabled ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-500 hover:text-red-600 hover:bg-red-50'}`}>
                              {user.disabled ? <Check className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                            </Button>
                            {presence[user.id] && (
                              <Button variant="ghost" size="sm" onClick={() => secAction(user, 'force-logout')}
                                data-testid={`user-force-logout-${user.id}`} title="Force logout from every device"
                                className="h-7 w-7 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50">
                                <LogOut className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </>)}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPanelAccessDialog(user)}
                            data-testid={`panel-access-${user.id}`}
                            className="h-7 w-7 p-0 text-slate-500 hover:text-purple-600 hover:bg-purple-50"
                            title="Panel Access"
                          >
                            <Shield className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setCopyDlg({ source: user, targets: [] })}
                            data-testid={`copy-access-${user.id}`} title="Copy this member's access to others"
                            className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50">
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPasswordDialog(user)}
                            data-testid={`password-user-${user.id}`}
                            className="h-7 w-7 p-0 text-slate-500 hover:text-amber-600 hover:bg-amber-50"
                            title="View / Change Password"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          {user.is_test_account && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setResetUser(user)}
                              data-testid={`reset-test-data-${user.id}`}
                              className="h-7 w-7 p-0 text-fuchsia-500 hover:text-fuchsia-700 hover:bg-fuchsia-50"
                              title="Reset test data"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(user)}
                            data-testid={`edit-user-${user.id}`}
                            className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                            title="Edit User"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          {user.email !== 'admin@taskflow.com' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              data-testid={`delete-user-${user.id}`}
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-700 hover:bg-red-50"
                              title="Delete User"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        <CopyAccessDialog open={!!copyDlg} onClose={() => setCopyDlg(null)} users={users}
          source={copyDlg?.source} targets={copyDlg?.targets} onDone={() => { fetchUsers(); setSelectedIds(new Set()); }} />

        {/* Password Dialog — admin view / change a member's password */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="max-w-md" data-testid="password-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-amber-600" />
                Manage Password
              </DialogTitle>
              <DialogDescription>
                {passwordTarget && (
                  <span>View or set the password for <strong>{passwordTarget.name}</strong> ({passwordTarget.email})</span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Current Password</Label>
                <div className="flex items-center rounded-md border bg-slate-50 px-3 h-9" data-testid="current-password-value">
                  <span className="text-sm font-mono flex-1 truncate">
                    {currentPassword ? (showCurrent ? currentPassword : "••••••••") : "Not recorded"}
                  </span>
                  {currentPassword && (
                    <button type="button" onClick={() => setShowCurrent(v => !v)} data-testid="toggle-current-password" className="text-slate-400 hover:text-slate-700">
                      {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                {!currentPassword && (
                  <p className="text-[11px] text-slate-500">This account's password was set before recording was enabled. Set a new one below to view it going forward.</p>
                )}
              </div>
              <form onSubmit={handleChangePassword} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="admin-new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="admin-new-password"
                      type={showNew ? "text" : "password"} noEye
                      data-testid="admin-new-password"
                      placeholder="Minimum 6 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button type="button" onClick={() => setShowNew(v => !v)} data-testid="toggle-new-password" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                      {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={passwordSaving}
                  data-testid="submit-change-password"
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                >
                  {passwordSaving ? "Saving..." : "Set New Password"}
                </Button>
              </form>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user details and panel access permissions
              </DialogDescription>
            </DialogHeader>
            {selectedUser && (
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Full Name</Label>
                    <Input
                      id="edit-name"
                      data-testid="edit-user-name"
                      value={selectedUser.name}
                      onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">Email</Label>
                    <Input
                      id="edit-email"
                      value={selectedUser.email}
                      disabled
                      className="bg-slate-100"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-team">Team</Label>
                    <Select 
                      onValueChange={(value) => setSelectedUser({ ...selectedUser, team: value })} 
                      value={selectedUser.team}
                    >
                      <SelectTrigger data-testid="edit-user-team">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(team => (
                          <SelectItem key={team.value} value={team.value}>{team.label}</SelectItem>
                        ))}
                        <SelectItem value="Admin">Admin Panel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-role">Role</Label>
                    <Select 
                      onValueChange={(value) => setSelectedUser(value === 'packer'
                        ? { ...selectedUser, role: 'member', is_packer: true, is_pp_user: false, is_query_team: false }
                        : (value === 'pp_user' || value === 'query_team')
                        ? { ...selectedUser, is_packer: false, role: 'member', is_pp_user: value === 'pp_user', is_query_team: value === 'query_team',
                            panel_access: { ...(selectedUser.panel_access || {}), processing_team: true },
                            processing_team_sub_panels: { ...(selectedUser.processing_team_sub_panels || {}), order_register: true } }
                        : { ...selectedUser, role: value, is_pp_user: false, is_query_team: false, is_packer: false })}
                      value={selectedUser.role === 'admin' ? 'admin' : selectedUser.is_packer ? 'packer' : selectedUser.is_pp_user ? 'pp_user' : selectedUser.is_query_team ? 'query_team' : selectedUser.role}
                      disabled={selectedUser.email === 'admin@taskflow.com'}
                    >
                      <SelectTrigger data-testid="edit-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="pp_user" data-testid="edit-user-role-pp">PP User (Pending Parcel User)</SelectItem>
                        <SelectItem value="query_team" data-testid="edit-user-role-query">Query Team (customer queries only)</SelectItem>
                        <SelectItem value="packer" data-testid="edit-user-role-packer">Packing Team (phone camera · photos only)</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {selectedUser.is_packer && selectedUser.role !== 'admin' && (
                  <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-3" data-testid="edit-user-packer-services">
                    <p className="mb-1 text-xs font-semibold text-teal-900">Allotted services — this packer sees only these parcels (none ticked = all)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {orderRegOptions.services.map(svc => (
                        <label key={svc} className="inline-flex cursor-pointer items-center gap-1 rounded border bg-white px-2 py-0.5 text-[11px]" data-testid={`edit-packer-svc-${svc}`}>
                          <input type="checkbox" checked={(selectedUser.packer_services || []).includes(svc)}
                            onChange={(e) => setSelectedUser(u => ({ ...u, packer_services: e.target.checked ? [...(u.packer_services || []), svc] : (u.packer_services || []).filter(x => x !== svc) }))} /> {svc}
                        </label>))}
                    </div>
                  </div>
                )}

                {selectedUser.email !== 'admin@taskflow.com' && (
                  <label className="flex items-start gap-2 p-3 rounded-lg border border-fuchsia-200 bg-fuchsia-50/60 cursor-pointer"
                         data-testid="edit-user-test-account">
                    <Checkbox checked={!!selectedUser.is_test_account}
                      onCheckedChange={(v) => setSelectedUser({ ...selectedUser, is_test_account: !!v })}
                      data-testid="edit-user-test-account-checkbox" />
                    <span className="text-xs text-slate-700">
                      <b className="text-fuchsia-700">Test account</b> — sandboxed: only sees/edits its own Order Register rows, tasks and notes;
                      cannot approve, reject or delete users; hidden from the Audit Log by default.
                    </span>
                  </label>
                )}

                {selectedUser.role !== 'admin' && (
                  <div className="space-y-2" data-testid="edit-user-pp-section">
                    <label className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50/70 cursor-pointer">
                      <Checkbox checked={!!selectedUser.is_pp_user}
                        onCheckedChange={(v) => setSelectedUser({ ...selectedUser, is_pp_user: !!v })}
                        data-testid="edit-user-pp-checkbox" />
                      <span className="text-xs text-slate-700">
                        <b className="text-amber-800">PP User (Pending Parcel User)</b> — in Order Register sees ONLY parcels that are not completed
                        and older than the window below (by order date). Editing needs their password, unlocks for 3 hours, and is audit-logged.
                        Empty service / country rights = all services; set them in Panel Access to narrow.
                      </span>
                    </label>
                    {selectedUser.is_pp_user && (
                      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs" data-testid="edit-user-pp-hours">
                        <span className="font-semibold text-amber-800">Sees parcels older than:</span>
                        {[48, 72, 96, 120].map(hh => (
                          <button key={hh} type="button" onClick={() => setSelectedUser({ ...selectedUser, pp_hours: hh })}
                            data-testid={`edit-user-pp-hours-${hh}`}
                            className={`rounded border px-2 py-1 font-semibold ${(selectedUser.pp_hours || 48) === hh ? "border-amber-600 bg-amber-100 text-amber-900" : "text-slate-600"}`}>
                            {hh} h</button>))}
                        <span className="text-slate-500">Team keeps parcels until the shortest PP window takes them.</span>
                      </div>)}
                    <label className="flex items-start gap-2 p-3 rounded-lg border border-orange-300 bg-orange-50/70 cursor-pointer">
                      <Checkbox checked={!!selectedUser.is_query_team}
                        onCheckedChange={(v) => setSelectedUser({ ...selectedUser, is_query_team: !!v })}
                        data-testid="edit-user-query-team-checkbox" />
                      <span className="text-xs text-slate-700">
                        <b className="text-orange-800">Query team</b> — contacts customers through the parcel Query thread
                        (notes, follow-ups, outcome). <b>Cannot modify any order</b> in the Order Register.
                      </span>
                    </label>
                    <label className="flex items-start gap-2 p-3 rounded-lg border border-indigo-200 bg-indigo-50/60 cursor-pointer">
                      <Checkbox checked={!!selectedUser.is_pp_user || !!selectedUser.pending_service_report}
                        disabled={!!selectedUser.is_pp_user}
                        onCheckedChange={(v) => setSelectedUser({ ...selectedUser, pending_service_report: !!v })}
                        data-testid="edit-user-psr-checkbox" />
                      <span className="text-xs text-slate-700">
                        <b className="text-indigo-700">Pending Parcels – Service-wise report</b> button in Order Register
                        (always on for admins and PP users).
                      </span>
                    </label>
                  </div>
                )}

                {/* Panel Access */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Panel Access</Label>
                  <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                    {panels.map(panel => (
                      <div key={panel.key} className="flex items-center justify-between py-1">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`edit-${panel.key}`}
                            data-testid={`edit-panel-${panel.key}`}
                            checked={selectedUser.panel_access?.[panel.key] || false}
                            onCheckedChange={(checked) => setSelectedUser({
                              ...selectedUser,
                              panel_access: { ...selectedUser.panel_access, [panel.key]: checked }
                            })}
                          />
                          <label htmlFor={`edit-${panel.key}`} className="text-sm font-medium cursor-pointer">
                            {panel.label}
                          </label>
                        </div>
                        <span className="text-xs text-slate-500">{panel.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Document Sub-Panel Access */}
                {selectedUser?.panel_access?.documents && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-purple-700">Documents Panel Permissions</Label>
                    <div className="border border-purple-200 rounded-lg p-3 space-y-2 bg-purple-50/50">
                      {documentSubPanels.map(sp => (
                        <div key={sp.key} className="flex items-center justify-between py-1">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`edit-doc-${sp.key}`}
                              data-testid={`edit-doc-panel-${sp.key}`}
                              checked={selectedUser?.document_sub_panels?.[sp.key] || false}
                              onCheckedChange={(checked) => setSelectedUser({
                                ...selectedUser,
                                document_sub_panels: { ...selectedUser.document_sub_panels, [sp.key]: checked }
                              })}
                            />
                            <label htmlFor={`edit-doc-${sp.key}`} className="text-sm font-medium cursor-pointer">
                              {sp.label}
                            </label>
                          </div>
                          <span className="text-xs text-slate-500">{sp.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 sticky bottom-[-1.5rem] -mx-6 -mb-6 px-6 py-3 bg-white border-t z-10 max-sm:bottom-[-1rem] max-sm:-mx-4 max-sm:-mb-4 max-sm:px-4">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setEditDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    data-testid="submit-edit-user"
                    className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Panel Access Dialog - Dedicated UI for managing panel access */}
        <Dialog open={panelAccessDialogOpen} onOpenChange={setPanelAccessDialogOpen}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Panel Access Control
              </DialogTitle>
              <DialogDescription>
                {selectedUser && (
                  <span>Configure which panels <strong>{selectedUser.name}</strong> ({selectedUser.email}) can access</span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Role Templates toolbar */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 flex items-center gap-2 flex-wrap"
                   data-testid="role-templates-toolbar">
                <span className="text-xs font-semibold text-purple-700 flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5" /> Role Templates:
                </span>
                <div className="flex-1 min-w-[180px]">
                  <Select onValueChange={applyTemplate}>
                    <SelectTrigger className="h-8 text-xs bg-white" data-testid="role-template-select">
                      <SelectValue placeholder={roleTemplates.length ? "Apply a template…" : "No templates saved yet"} />
                    </SelectTrigger>
                    <SelectContent>
                      {roleTemplates.length === 0 && (
                        <div className="px-2 py-3 text-xs text-slate-400 italic">No templates yet — save one below</div>
                      )}
                      {roleTemplates.map(t => (
                        <SelectItem key={t.id} value={t.id} data-testid={`role-template-option-${t.id}`}>
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">{t.name}</span>
                            {t.description && <span className="text-[10px] text-slate-500">{t.description}</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" variant="outline" className="h-8 text-[11px]"
                        onClick={() => setSaveTemplateOpen(true)}
                        data-testid="save-as-template">
                  <Check className="w-3 h-3 mr-1" /> Save current as template
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-[11px] text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setManageTemplatesOpen(true)}
                        data-testid="manage-templates">
                  <X className="w-3 h-3 mr-1" /> Manage
                </Button>
              </div>

              <p className="text-sm text-slate-600">
                Grant access <strong>independently</strong> — you can toggle a whole panel,
                only specific sub-panels, or mix. Sub-panels shown here work even when the
                parent panel is off; the sidebar will auto-show the parent so the user can reach them.
              </p>
              
              <div className="space-y-3 max-h-96 overflow-y-auto max-sm:max-h-none max-sm:overflow-visible">
                {panels.map(panel => {
                  const isDoc = panel.key === 'documents';
                  const isProc = panel.key === 'processing_team';
                  const subList = isDoc ? documentSubPanels : (isProc ? processingSubPanels : []);
                  const subState = isDoc ? selectedUserDocSubPanels : (isProc ? selectedUserProcSubPanels : {});
                  const setSubState = isDoc ? setSelectedUserDocSubPanels : setSelectedUserProcSubPanels;
                  const grantedCount = subList.filter(sp => subState[sp.key] === true).length;
                  const totalCount = subList.length;
                  const isParentOnly = selectedUserPanelAccess[panel.key] === true
                                        && subList.length > 0
                                        && grantedCount === 0;
                  return (
                  <div key={panel.key}>
                    <div 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isParentOnly
                          ? 'border-red-300 bg-red-50'
                          : selectedUserPanelAccess[panel.key] 
                            ? 'border-green-200 bg-green-50' 
                            : (grantedCount > 0
                                ? 'border-amber-200 bg-amber-50'
                                : 'border-slate-200 bg-slate-50')
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id={`access-${panel.key}`}
                          checked={selectedUserPanelAccess[panel.key] || false}
                          onCheckedChange={(checked) => 
                            setSelectedUserPanelAccess(prev => ({
                              ...prev,
                              [panel.key]: checked
                            }))
                          }
                          data-testid={`panel-access-checkbox-${panel.key}`}
                        />
                        <div>
                          <label htmlFor={`access-${panel.key}`} className="text-sm font-medium cursor-pointer">
                            {panel.label}
                          </label>
                          <p className="text-xs text-slate-500">{panel.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {subList.length > 0 && (
                          <Badge
                            className={grantedCount > 0
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-slate-100 text-slate-500'}
                            data-testid={`sub-panel-count-${panel.key}`}
                          >
                            {grantedCount}/{totalCount} sub
                          </Badge>
                        )}
                        <Badge className={selectedUserPanelAccess[panel.key] ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                          {selectedUserPanelAccess[panel.key] ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                    </div>

                    {isParentOnly && (
                      <div className="ml-6 mt-1 mb-1 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2"
                           data-testid={`parent-only-warning-${panel.key}`}>
                        <svg className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" />
                        </svg>
                        <div className="text-[11px] text-red-800">
                          <strong>User will see &quot;Access Denied&quot;.</strong>
                          {' '}You&apos;ve enabled <strong>{panel.label}</strong> but haven&apos;t ticked any of its {totalCount} sub-panels below.
                          Click <strong>&quot;Grant Full Panel&quot;</strong> to enable everything, or tick specific sub-panels.
                        </div>
                      </div>
                    )}

                    {/* Sub-panels are ALWAYS visible for parents that have them, so
                        admin can grant an individual sub-panel without granting the
                        whole parent panel. */}
                    {subList.length > 0 && (
                      <div className={`ml-6 mt-1 space-y-1 border-l-2 pl-3 py-1 ${
                        isDoc ? 'border-purple-200' : 'border-emerald-200'
                      }`}>
                        <div className="flex flex-wrap items-center justify-between gap-1 mb-1">
                          <p className={`text-xs font-medium ${isDoc ? 'text-purple-600' : 'text-emerald-600'}`}>
                            {isDoc ? 'Document Sub-Panels' : 'Processing Team Sub-Panels'}
                          </p>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[11px] text-green-700 border-green-200 hover:bg-green-50"
                              onClick={() => {
                                // Grant full panel: parent + every sub-panel
                                setSelectedUserPanelAccess(prev => ({ ...prev, [panel.key]: true }));
                                setSubState(prev => {
                                  const next = { ...prev };
                                  subList.forEach(sp => { next[sp.key] = true; });
                                  return next;
                                });
                              }}
                              data-testid={`grant-full-${panel.key}`}
                            >
                              <Check className="w-3 h-3 mr-0.5" /> Grant Full Panel
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[11px] text-slate-600 border-slate-200 hover:bg-slate-50"
                              onClick={() => {
                                setSubState(prev => {
                                  const next = { ...prev };
                                  subList.forEach(sp => { next[sp.key] = true; });
                                  return next;
                                });
                              }}
                              data-testid={`grant-all-sub-${panel.key}`}
                            >
                              All Sub
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[11px] text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                setSubState(prev => {
                                  const next = { ...prev };
                                  subList.forEach(sp => { next[sp.key] = false; });
                                  return next;
                                });
                              }}
                              data-testid={`ungrant-all-sub-${panel.key}`}
                            >
                              <X className="w-3 h-3 mr-0.5" /> None
                            </Button>
                          </div>
                        </div>
                        {subList.map(sp => (
                          <div key={sp.key}>
                          <div
                            className={`flex items-center justify-between p-2 rounded border text-sm ${
                              subState[sp.key]
                                ? (isDoc ? 'border-purple-200 bg-purple-50' : 'border-emerald-200 bg-emerald-50')
                                : 'border-slate-200 bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`sub-access-${panel.key}-${sp.key}`}
                                checked={subState[sp.key] || false}
                                onCheckedChange={(checked) =>
                                  setSubState(prev => ({
                                    ...prev,
                                    [sp.key]: checked
                                  }))
                                }
                                data-testid={isDoc
                                  ? `sub-panel-access-checkbox-${sp.key}`
                                  : `proc-sub-panel-checkbox-${sp.key}`}
                              />
                              <label htmlFor={`sub-access-${panel.key}-${sp.key}`} className="text-xs font-medium cursor-pointer">
                                {sp.label}
                              </label>
                            </div>
                            <span className="text-xs text-slate-400">{sp.description}</span>
                          </div>
                          {sp.key === 'order_register' && subState[sp.key] === true && (
                            <div className="mt-1 ml-5 p-2.5 rounded border border-amber-200 bg-amber-50" data-testid="orderreg-scope-box">
                              <p className="text-[11px] font-semibold text-amber-700 mb-1.5">
                                Order Register scoping — nothing ticked = user sees NO orders
                              </p>
                              {/* Iter 181 — one-click presets */}
                              <div className="mb-2" data-testid="orderreg-scope-presets">
                                <p className="text-[10px] font-medium text-amber-800 mb-1 uppercase tracking-wide">Quick presets</p>
                                <div className="flex flex-wrap gap-1">
                                  {ORDERREG_PRESETS.map(p => (
                                    <button key={p.key} type="button"
                                      onClick={() => applyOrderRegPreset(p)}
                                      className="text-[10px] px-2 py-0.5 rounded-full border border-amber-400 bg-white text-amber-800 hover:bg-amber-100 font-medium transition"
                                      data-testid={`orderreg-preset-${p.key}`}
                                      title={`Services: ${p.services.join(', ')} · Countries: ${p.countries.join(', ')}`}>
                                      {p.label}
                                    </button>
                                  ))}
                                  <button type="button"
                                    onClick={() => { setOrderRegServices([]); setOrderRegCountries([]); }}
                                    className="text-[10px] px-2 py-0.5 rounded-full border border-slate-300 bg-white text-slate-500 hover:bg-slate-100 font-medium transition"
                                    data-testid="orderreg-preset-clear">
                                    Clear
                                  </button>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div data-testid="orderreg-scope-services">
                                  <p className="text-[11px] font-medium text-slate-500 mb-1">Allowed Services</p>
                                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                                    {orderRegOptions.services.map(svc => (
                                      <label key={svc} className="flex items-center gap-1 text-xs cursor-pointer">
                                        <Checkbox
                                          checked={orderRegServices.includes(svc)}
                                          onCheckedChange={(c) =>
                                            setOrderRegServices(prev => c ? [...prev, svc] : prev.filter(x => x !== svc))
                                          }
                                          data-testid={`orderreg-scope-service-${svc}`}
                                        />
                                        {svc}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div data-testid="orderreg-scope-countries">
                                  <p className="text-[11px] font-medium text-slate-500 mb-1">Allowed Countries</p>
                                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                                    {orderRegOptions.countryGroups.map(grp => (
                                      <label key={grp} className="flex items-center gap-1 text-xs cursor-pointer">
                                        <Checkbox
                                          checked={orderRegCountries.includes(grp)}
                                          onCheckedChange={(c) =>
                                            setOrderRegCountries(prev => c ? [...prev, grp] : prev.filter(x => x !== grp))
                                          }
                                          data-testid={`orderreg-scope-country-${grp}`}
                                        />
                                        {grp.replace(/_/g, ' ')}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          {sp.key === 'order_register' && subState[sp.key] === true && (
                            <>
                            <OrderRegRightsBox access={orderRegAccess} setAccess={setOrderRegAccess}
                              allowed={orderRegButtons} setAllowed={setOrderRegButtons} />
                            <ViewServicesBox services={orderRegOptions.services} editServices={orderRegServices}
                              value={orderRegViewServices} setValue={setOrderRegViewServices} />
                            </>
                          )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedUserPanelAccess(
                      panels.reduce((acc, p) => ({ ...acc, [p.key]: true }), {})
                    );
                    setSelectedUserDocSubPanels(
                      documentSubPanels.reduce((acc, sp) => ({ ...acc, [sp.key]: true }), {})
                    );
                    setSelectedUserProcSubPanels(
                      processingSubPanels.reduce((acc, sp) => ({ ...acc, [sp.key]: true }), {})
                    );
                  }}
                  className="text-green-600"
                  data-testid="enable-all-panels"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Enable All (Panels + Sub)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedUserPanelAccess(
                      panels.reduce((acc, p) => ({ ...acc, [p.key]: false }), {})
                    );
                    setSelectedUserDocSubPanels(
                      documentSubPanels.reduce((acc, sp) => ({ ...acc, [sp.key]: false }), {})
                    );
                    setSelectedUserProcSubPanels(
                      processingSubPanels.reduce((acc, sp) => ({ ...acc, [sp.key]: false }), {})
                    );
                  }}
                  className="text-red-600"
                  data-testid="disable-all-panels"
                >
                  <X className="w-4 h-4 mr-1" />
                  Disable All
                </Button>
              </div>
              <PanelRightsBox value={panelRights} setValue={setPanelRights} />
            </div>

            <div className="flex gap-2 sticky bottom-[-1.5rem] -mx-6 -mb-6 px-6 py-3 bg-white border-t z-10 max-sm:bottom-[-1rem] max-sm:-mx-4 max-sm:-mb-4 max-sm:px-4">
              <Button 
                variant="outline"
                onClick={() => setPanelAccessDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdatePanelAccess}
                data-testid="save-panel-access"
                className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
              >
                <Shield className="w-4 h-4 mr-2" />
                Save Panel Access
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Save-as-Template dialog */}
        <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
          <DialogContent className="max-w-md" data-testid="save-template-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" /> Save as Role Template
              </DialogTitle>
              <DialogDescription>
                Save the current permission set as a reusable template. You can then
                apply it to any user with one click.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Template name *</Label>
                <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                       placeholder="e.g. Full Processing Team"
                       data-testid="template-name-input" />
              </div>
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Input value={templateDesc} onChange={(e) => setTemplateDesc(e.target.value)}
                       placeholder="Short note about what this role covers"
                       data-testid="template-desc-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSaveTemplateOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={saveAsTemplate} disabled={!templateName.trim()}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="save-template-submit">
                <Check className="w-4 h-4 mr-1" /> Save Template
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Manage templates dialog */}
        <Dialog open={manageTemplatesOpen} onOpenChange={setManageTemplatesOpen}>
          <DialogContent className="max-w-lg" data-testid="manage-templates-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" /> Manage Role Templates
              </DialogTitle>
              <DialogDescription>
                {roleTemplates.length} saved template{roleTemplates.length === 1 ? "" : "s"}.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto space-y-2 py-2">
              {roleTemplates.length === 0 ? (
                <div className="text-xs text-slate-400 italic p-4 text-center">
                  No templates saved yet.
                </div>
              ) : roleTemplates.map(t => {
                const paCount = Object.values(t.panel_access || {}).filter(v => v === true).length;
                const dsCount = Object.values(t.document_sub_panels || {}).filter(v => v === true).length;
                const psCount = Object.values(t.processing_team_sub_panels || {}).filter(v => v === true).length;
                return (
                  <div key={t.id} className="border rounded p-2.5 flex items-start justify-between gap-2 hover:bg-slate-50"
                       data-testid={`manage-template-row-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                      {t.description && <div className="text-[11px] text-slate-500">{t.description}</div>}
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <Badge className="bg-blue-50 text-blue-700 text-[10px]">{paCount} panel{paCount === 1 ? "" : "s"}</Badge>
                        <Badge className="bg-purple-50 text-purple-700 text-[10px]">{dsCount} doc sub</Badge>
                        <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">{psCount} proc sub</Badge>
                      </div>
                    </div>
                    <Button size="sm" variant="outline"
                            className="h-7 px-2 text-[11px] text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => deleteTemplate(t)}
                            data-testid={`delete-template-${t.id}`}>
                      <Trash2 className="w-3 h-3 mr-0.5" /> Delete
                    </Button>
                  </div>
                );
              })}
            </div>
            <Button variant="outline" onClick={() => setManageTemplatesOpen(false)}>Close</Button>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
      </>
      )}
    </div>
  );
};

export default AdminUserManagement;
